import React from 'react';
import ActionWall from 'shared/components/ActionWall';
import { useTranslation } from 'react-i18next';
import { Divider, Grid, Skeleton, Stack, Typography } from '@mui/material';
import { ReactComponent as SuccessInterviewIcon } from 'assets/checkSuccess.svg';
import InterviewModalHeader from './InterviewPanelModalHeader';
import { colors } from 'styles/colors';

export interface ISuccessInterviewerPanelModal {
  id: string;
  reqId: string;
  open: boolean;
  jobTitle: string;
  showLoader: boolean;
  handleClose: () => void;
}

const SuccessInterviewerPanelModal = ({
  id,
  reqId,
  open,
  jobTitle,
  showLoader,
  handleClose,
}: ISuccessInterviewerPanelModal) => {
  const { t: translate } = useTranslation();
  return (
    <ActionWall
      dialogId={id}
      open={open}
      closeIconButton={false}
      successDisabled={showLoader}
      titleClassName='kfic2-panelTitleBold'
      headerContent={
        <InterviewModalHeader
          reqId={reqId}
          title={translate('invitePeopleToInterviewForThisRequisition')}
          subTitle={jobTitle}
        />
      }
      id={`${id}-modal`}
      tabIndex={0}
      aria-label={translate('makeDecision')}
      centerAlignment={true}
      dialogPaperSxProps={{
        p: 2.5,
        m: 0,
        maxWidth: '53.25rem',
      }}
      dialogContentSxProps={{
        mb: 0,
        pb: 0,
        pt: '1.25rem !important',
        maxHeight: 'calc(100% - 9.75rem)',
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
          backgroundColor: colors?.tertiary.white,
          width: '1rem',
        },
        '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
          borderRadius: '0.6rem',
          backgroundColor: colors?.background.darkGray,
          border: `0.25rem ${colors?.tertiary.white} solid `,
        },
      }}
      dialogTitleSxProps={{
        px: 0,
        mb: 0,
        zIndex: 1,
        fontSize: '20px',
        fontFamily: 'Gotham',
        lineHeight: '28px',
        fontweight: 700,
      }}
      closeButtonStyleProps={{
        top: 0,
        padding: 0,
      }}
      closeIconStyleProps={{
        width: '1.25rem',
        height: '1.25rem',
      }}
      sxTitle={{
        mt: 0,
      }}
      saveBtnSxProps={{}}
      footerContent={
        <Grid container>
          <Divider
            id='interview-modal-footer-divider'
            data-testid='interview-modal-footer-divider'
            sx={{
              mt: ({ typography }) => typography.pxToRem(24),
              mb: ({ typography }) => typography.pxToRem(0),
              width: '100%',
            }}
          />
        </Grid>
      }
      onSuccess={() => {
        handleClose();
      }}
      outlinedCancelButton
      dialogActionsSxProps={{ mt: 1, width: 'fit-content', pr: 0 }}
      dialogActionsPDivSxProps={{ display: 'flex' }}
      successLabel={translate('close')}
    >
      <Stack
        justifyContent={'center'}
        alignItems={'center'}
        spacing={3.75}
        direction={'column'}
        sx={{
          pb: 10,
        }}
      >
        {showLoader ? (
          <Skeleton
            variant='circular'
            data-testid='success-decision-modal-loader'
            animation='wave'
            width='2.25rem'
            height='2.25rem'
            sx={{
              marginTop: '1.25rem',
              bgcolor: (theme) => theme.palette.neutral?.main,
            }}
          />
        ) : (
          <SuccessInterviewIcon
            style={{
              marginTop: '1.25rem',
            }}
          />
        )}

        <Typography
          className='kfic2-headerBold'
          sx={{
            mt: ({ typography }) => `${typography.pxToRem(19)} !important`,
          }}
        >
          {showLoader ? (
            <Skeleton
              variant='rectangular'
              animation='wave'
              width={350}
              height={32}
              sx={{
                bgcolor: (theme) => theme.palette.neutral?.main,
              }}
            />
          ) : (
            translate('interviewersAddedSuccessfully')
          )}
        </Typography>
      </Stack>
    </ActionWall>
  );
};

export default SuccessInterviewerPanelModal;
